import { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import RatingDistributionHistogram from "./RatingDistributionHistogram";
import RatingDeviations from "./RatingDeviations";
import UserFilmsCount from "./UserFilmsCount";
import { useRatingsDistribution } from "../hooks/useRatingsDistribution";
import { useTopFilmsByYear } from "../hooks/useTopFilmsByYear";
import { useRatingDeviation } from "../hooks/useRatingDeviation";
import Spinner from "./Spinner";
import MovieBarChart from "./MovieBarChart";
import { cn } from "../lib/utils";
import { useMediaQuery } from "../hooks/useMediaQuery";

const FIRST_YEAR = 1910;

const TABS = [
  { value: "rated", label: "Highest rated" },
  { value: "watched", label: "Most watched" },
] as const;

// Out-of-range values fall back to all-years so the <select> never holds a
// value with no matching <option>, and the server never sees a year it 400s on.
const parseYear = (raw: string | null, currentYear: number): number | null => {
  if (!raw) return null;
  const year = Number(raw);
  return Number.isInteger(year) && year >= FIRST_YEAR && year <= currentYear
    ? year
    : null;
};

const Dashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<"rated" | "watched">("rated");
  // Above md both panels are shown side by side, so there is no tablist to
  // label them and the tab roles have to go with it.
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const tablistRef = useRef<HTMLDivElement>(null);

  const currentYear = new Date().getFullYear();
  const selectedYear = parseYear(searchParams.get("year"), currentYear);

  // All-time on purpose: a rating-activity histogram scoped by film release
  // year reads as the wrong thing, and we have no watch/rating date to scope by.
  const { data: allRatings, loading: ratingsLoading } =
    useRatingsDistribution(null);
  const { topRated, topWatched, loading, error } =
    useTopFilmsByYear(selectedYear);
  const {
    over,
    under,
    loading: deviationLoading,
  } = useRatingDeviation(selectedYear);

  // Mirror the top-films spinner: only block on the very first fetch, keep the
  // cards mounted (dimmed) while a new year loads.
  const deviationInitialLoad =
    deviationLoading && over.length === 0 && under.length === 0;

  const years: number[] = [];
  for (let y = currentYear; y >= FIRST_YEAR; y--) years.push(y);

  const ratedHeading =
    selectedYear === null
      ? "Our highest rated movies (20+ ratings)"
      : `Top rated movies of ${selectedYear} (5+ ratings)`;
  const watchedHeading =
    selectedYear === null
      ? "Our most watched movies"
      : `Most watched movies of ${selectedYear}`;
  const ratedEmpty =
    selectedYear === null
      ? "No rated films yet."
      : `No films released in ${selectedYear} have enough ratings yet.`;
  const watchedEmpty =
    selectedYear === null
      ? "No watched films yet."
      : `No films released in ${selectedYear} watched yet.`;

  // Keep the lists mounted (dimmed) while a new year loads to avoid a layout
  // jump; only show the full-page spinner before the first results arrive.
  const isInitialLoad =
    loading && topRated.length === 0 && topWatched.length === 0;

  // onKeyDown sits on each tab, not the tablist: the container is not
  // focusable, and hanging keyboard handling off it only looks correct.
  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const from = TABS.findIndex((tab) => tab.value === activeTab);
    let next: number;
    if (event.key === "ArrowRight") next = (from + 1) % TABS.length;
    else if (event.key === "ArrowLeft")
      next = (from - 1 + TABS.length) % TABS.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = TABS.length - 1;
    else return;

    event.preventDefault();
    setActiveTab(TABS[next]!.value);
    tablistRef.current?.querySelectorAll("button")[next]?.focus();
  };

  return (
    <div>
      {/* Visually absent by design: the page opens on the welcome copy, and the
          logo is the visible title. The hierarchy still needs a root. */}
      <h1 className="sr-only">Discord stats</h1>
      <UserFilmsCount />

      <h2 className="subheading">How we rated all of our movies:</h2>
      {/* min-h matches the md histogram's fixed height (h-32) so swapping in the
          loading spinner doesn't collapse the row and shift the page. */}
      <div className="flex min-h-32 items-center justify-center mb-4">
        {ratingsLoading ? (
          <Spinner />
        ) : (
          <RatingDistributionHistogram size="md" distribution={allRatings} />
        )}
      </div>

      {/* Scopes only the release-year sections below (deviations + lists); the
          count and histogram above are all-time. */}
      <div className="flex items-center gap-2 mb-6 justify-center">
        <label htmlFor="stats-year" className="body-text">
          Filter by release year:
        </label>
        <select
          id="stats-year"
          className="input-field"
          value={selectedYear ?? ""}
          onChange={(e) => {
            const { value } = e.target;
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              if (value) next.set("year", value);
              else next.delete("year");
              return next;
            });
          }}
        >
          <option value="">All years</option>
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>

      <h2 className="subheading max-md:text-base">Our biggest deviations</h2>
      <div className="mb-8">
        {deviationInitialLoad ? (
          <div className="flex justify-center">
            <Spinner />
          </div>
        ) : (
          <RatingDeviations
            over={over}
            under={under}
            loading={deviationLoading}
          />
        )}
      </div>

      {error ? (
        <p role="alert" className="body-text -prose text-letterboxd-error">
          {error}
        </p>
      ) : isInitialLoad ? (
        <Spinner />
      ) : (
        <>
          {!isDesktop && (
            <div
              ref={tablistRef}
              role="tablist"
              aria-label="Top films category"
              className="flex mb-4 border-b border-letterboxd-border"
            >
              {TABS.map(({ value, label }) => (
                <button
                  key={value}
                  role="tab"
                  id={`tab-${value}`}
                  aria-selected={activeTab === value}
                  aria-controls={`panel-${value}`}
                  tabIndex={activeTab === value ? 0 : -1}
                  onClick={() => setActiveTab(value)}
                  onKeyDown={handleTabKeyDown}
                  className={cn(
                    "flex-1 -mb-px border-b-2 py-2 text-center font-medium transition-colors",
                    activeTab === value
                      ? "border-letterboxd-accent text-letterboxd-text-primary"
                      : "border-transparent text-letterboxd-text-secondary",
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          <div
            aria-busy={loading}
            className={cn(
              "flex flex-row justify-between max-md:justify-center max-md:flex-col m-auto w-full transition-opacity",
              loading && "opacity-50",
            )}
          >
            <div
              id={`panel-rated`}
              role={isDesktop ? undefined : "tabpanel"}
              aria-labelledby={isDesktop ? undefined : `tab-rated`}
              className={cn(
                "flex-1 min-w-0 md:mr-2",
                activeTab !== "rated" && "max-md:hidden",
              )}
            >
              <h2 className="subheading max-md:text-base">{ratedHeading}</h2>
              <MovieBarChart
                movies={topRated}
                showRating={true}
                emptyMessage={ratedEmpty}
              />
            </div>

            <div
              id={`panel-watched`}
              role={isDesktop ? undefined : "tabpanel"}
              aria-labelledby={isDesktop ? undefined : `tab-watched`}
              className={cn(
                "flex-1 min-w-0 md:ml-2",
                activeTab !== "watched" && "max-md:hidden",
              )}
            >
              <h2 className="subheading max-md:text-base">{watchedHeading}</h2>
              <MovieBarChart
                movies={topWatched}
                showCount={true}
                emptyMessage={watchedEmpty}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
